import { QueryClient } from '@tanstack/react-query'
import {
  createRouter as createTanStackRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'

import ErrorPage from './components/ErrorPage'
import NotFound from './components/NotFound'
import { RouteProgress } from './components/RouteProgress'
import { captureException } from './lib/analytics/posthog'
import { routeTree } from './routeTree.gen'

/**
 * Reports render errors caught by TanStack Router's error boundaries to
 * PostHog error tracking. Uncaught errors and unhandled rejections outside
 * React are captured automatically by the global handlers `posthog.init`
 * installs (`capture_exceptions: true`); error boundaries swallow render
 * errors before they reach those handlers, so we forward them here
 * explicitly, then fall back to the default error UI.
 */
function RouterErrorComponent(props: ErrorComponentProps) {
  captureException(props.error)
  return <ErrorPage {...props} />
}

export function getRouter() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000 },
    },
  })

  const router = createTanStackRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreload: 'intent',
    // 0 is correct with TanStack Query: Query's own staleTime governs freshness
    // and loaders go through ensureQueryData. NOTE: hover-preload runs
    // `_authenticated`'s beforeLoad, and the click then runs it AGAIN, so
    // `getAuthContext` fires twice per navigation. Raising this to 2s did not stop
    // that in testing; the duplicate is still open.
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: RouterErrorComponent,
    defaultNotFoundComponent: NotFound,
    // Route loaders now block navigation on their gating query, so a slow hop
    // needs feedback. `defaultPendingMs` is the grace period before showing
    // anything: below it the navigation lands first and a flashed bar would be
    // pure noise. `defaultPendingMinMs` then keeps the bar up long enough to
    // read once shown, instead of blinking.
    defaultPendingComponent: RouteProgress,
    defaultPendingMs: 300,
    defaultPendingMinMs: 300,
  })

  // PostHog is initialized by `<PostHogProvider>` in `__root.tsx`. It
  // autocaptures `$pageview` on its own, including client-side route changes,
  // so there's no router-tracing integration to wire up here.

  setupRouterSsrQueryIntegration({ router, queryClient })

  return router
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
